import { type ReactNode } from 'react'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import { checkAppBranding } from '@/lib/formatting'
import { PREFETCH } from '@/lib/prefetch'
import { cn, getBrandColors } from '@/lib/utils'
import type { BestCtaData } from '@/app/(app)/compare/[slug]/client-fetchers'

type BestCtaProps = BestCtaData & {
  className?: string
  shorten?: boolean
}

const SHORT_LABEL = 'Discover best apps'

export default function BestCta({
  apps,
  category,
  className,
  shorten = false,
}: BestCtaProps): ReactNode {
  if (!apps.length || !category.slug) return null
  const shortLabel = 'Discover best apps'
  const fullLabel = `Discover best ${category.nameLabelPlural || 'apps'}`

  const label = shorten ? shortLabel : fullLabel

  return (
    <Link
      href={`/best/${category.slug}`}
      prefetch={PREFETCH}
      aria-label={fullLabel}
      className={cn('group flex items-center gap-7', className)}
    >
      <div className="flex shrink-0 items-center">
        {apps.map((app, index) => {
          const { appIcon, appGlyph } = checkAppBranding(app)
          const { backgroundColor } = getBrandColors(app)

          return (
            <AppIcon
              key={app.id}
              icon={appIcon}
              glyph={appGlyph}
              name={app.name}
              background={backgroundColor}
              size={32}
              transition={false}
              className="-mr-3"
              style={{ zIndex: apps.length - index }}
            />
          )
        })}
      </div>
      <Pill as="span" style="primary" className="min-w-0 flex-1">
        <span className="truncate sm:hidden">{shortLabel}</span>
        <span className="hidden truncate sm:block">{label}</span>
      </Pill>
    </Link>
  )
}
